import {
  getAiInferencePresignedUrl,
  runAiInference,
} from '../../api/aiInference';
import { resolveRoomObjectImageSrcForDisplay } from '../roomObjectImage';
import { addTask } from '../../services/taskResolver';
import {
  snapFlythroughCameraAngle,
  validateFlythroughCameraAngleSequence,
} from './flythroughCameraAngles';
import {
  clampFlythroughRoi,
  FLYTHROUGH_MOTION_MODES,
  FLYTHROUGH_MOTION_PROMPT_FIELDS_DEFAULT,
  FLYTHROUGH_MOTION_PROMPT_MAX_LENGTH,
  FLYTHROUGH_PREVIEW_DURATION_SECONDS,
  FLYTHROUGH_PREVIEW_FPS,
  FLYTHROUGH_PREVIEW_RESOLUTION,
  FLYTHROUGH_ROI_FULL_SCREEN,
  resolveFlythroughFinalDurationSeconds,
  resolveFlythroughFpsApiValue,
  resolveFlythroughResolutionApiValue,
  resolveFlythroughResolutionIdFromApiValue,
} from './flythroughVideoConfig';
import { formatInferenceStatusLabel } from './roomImageRenderInference';
import {
  isFlythroughThumbnailPresignUnsupported,
  rememberFlythroughPlaybackUrl,
} from './flythroughHistoryDisplay';

export function extractFlythroughTaskResultKeys(data) {
  const taskResult = data?.taskResult ?? data?.result;
  if (taskResult == null) {
    return { videoS3Key: null, thumbnailS3Key: null };
  }
  if (typeof taskResult === 'string' && taskResult.trim()) {
    return { videoS3Key: taskResult.trim(), thumbnailS3Key: null };
  }

  const videoKey =
    taskResult?.resultVideoS3Key ??
    taskResult?.result_video_s3_key ??
    taskResult?.resultS3Key ??
    taskResult?.result_s3_key;
  const thumbnailKey = taskResult?.thumbnailS3Key ?? taskResult?.thumbnail_s3_key;

  return {
    videoS3Key: typeof videoKey === 'string' && videoKey.trim() ? videoKey.trim() : null,
    thumbnailS3Key:
      typeof thumbnailKey === 'string' && thumbnailKey.trim() ? thumbnailKey.trim() : null,
  };
}

export function buildFlythroughRoiPayload(roiMode, roi) {
  if (roiMode === 'full_screen') return null;
  return clampFlythroughRoi(roi);
}

function normalizeFlythroughPromptFragment(value) {
  return String(value ?? '')
    .trim()
    .replace(/[.,]$/, '');
}

function formatFlythroughScenePhrase(scene) {
  if (!scene) return '';
  if (/^(a|an|the)\s+/i.test(scene)) return scene;
  return `${/^[aeiou]/i.test(scene) ? 'an' : 'a'} ${scene}`;
}

function formatFlythroughMotionSpeedPhrase(speed) {
  if (!speed) return '';
  const normalized = speed.toLowerCase();
  const presets = {
    slow: 'at a slow, deliberate pace',
    moderate: 'at a steady, moderate pace',
    medium: 'at a steady, moderate pace',
    fast: 'at a brisk pace',
    quick: 'at a brisk pace',
  };
  if (presets[normalized]) return presets[normalized];
  if (/\bpace\b/i.test(speed)) {
    return /^at\s+/i.test(speed) ? speed : `at ${speed}`;
  }
  return `at a ${normalized} pace`;
}

function normalizeFlythroughCameraVerbPhrase(movement) {
  let text = movement
    .replace(/^the\s+camera\s+/i, '')
    .replace(/^camera\s+/i, '')
    .trim();

  const leadingAdverbMatch = text.match(
    /^(slowly|slow|gently|gentle|smoothly|smooth|quickly|quick|rapidly|fast)\s+(.+)$/i,
  );
  let adverb = '';
  if (leadingAdverbMatch) {
    const rawAdverb = leadingAdverbMatch[1].toLowerCase();
    adverb =
      rawAdverb === 'gentle' || rawAdverb === 'smooth'
        ? 'gently'
        : rawAdverb === 'slow'
          ? 'slowly'
          : rawAdverb === 'quick' || rawAdverb === 'fast' || rawAdverb === 'rapidly'
            ? 'quickly'
            : rawAdverb;
    text = leadingAdverbMatch[2];
  }

  text = text
    .replace(/\bto\s+(the\s+)?(left|right)\b/gi, 'to the $2')
    .replace(/\borbit\s+(the\s+)?(left|right)\b/gi, 'orbit to the $2');

  const verbPatterns = [
    [/^orbits?\s+to\s+the\s+(left|right)$/i, (match) => `orbits to the ${match[1].toLowerCase()}`],
    [/^orbit\s+to\s+the\s+(left|right)$/i, (match) => `orbits to the ${match[1].toLowerCase()}`],
    [/^orbit\s+to\s+(left|right)$/i, (match) => `orbits to the ${match[1].toLowerCase()}`],
    [/^dolly\s+(in|out)$/i, (match) => `dollies ${match[1].toLowerCase()}`],
    [/^jib\s+(up|down)$/i, (match) => `jibs ${match[1].toLowerCase()}`],
    [/^pans?\s+to\s+the\s+(left|right)$/i, (match) => `pans to the ${match[1].toLowerCase()}`],
    [/^pans?\s+(left|right|up|down)$/i, (match) => `pans ${match[1].toLowerCase()}`],
    [/^tilts?\s+(up|down)$/i, (match) => `tilts ${match[1].toLowerCase()}`],
    [/^zooms?\s+(in|out)$/i, (match) => `zooms ${match[1].toLowerCase()}`],
    [/^tracks?\s+(left|right|forward|backward|back)$/i, (match) => `tracks ${match[1].toLowerCase()}`],
    [/^push(?:es)?\s+(in|forward)$/i, () => 'pushes in'],
    [/^pull(?:s)?\s+(out|back)$/i, () => 'pulls back'],
    [
      /^(orbiting|dollying|panning|tilting|zooming|tracking|jibing|craning|pushing|pulling)\b/i,
      (match) => match[0].charAt(0).toLowerCase() + match[0].slice(1),
    ],
  ];

  let verbPhrase = text.charAt(0).toLowerCase() + text.slice(1);
  for (const [pattern, format] of verbPatterns) {
    const match = text.match(pattern);
    if (match) {
      verbPhrase = format(match);
      break;
    }
  }

  if (adverb) {
    return `the camera ${adverb} ${verbPhrase}`;
  }
  return `the camera ${verbPhrase}`;
}

function capitalizeSentence(text) {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function buildFlythroughMotionPromptFromFields(fields = {}) {
  const scene = normalizeFlythroughPromptFragment(fields.scene);
  const cameraMovement = normalizeFlythroughPromptFragment(fields.cameraMovement);
  const motionSpeed = normalizeFlythroughPromptFragment(fields.motionSpeed);
  const style = normalizeFlythroughPromptFragment(fields.style);

  if (!scene && !cameraMovement && !motionSpeed && !style) return '';

  const scenePhrase = formatFlythroughScenePhrase(scene);
  const cameraPhrase = cameraMovement ? normalizeFlythroughCameraVerbPhrase(cameraMovement) : '';
  const speedPhrase = formatFlythroughMotionSpeedPhrase(motionSpeed);
  const styleAdj = style ? style.toLowerCase() : '';
  const flythroughLabel = styleAdj ? `${styleAdj} flythrough` : 'flythrough';

  if (scenePhrase && cameraPhrase) {
    let sentence = `a ${flythroughLabel} of ${scenePhrase} as ${cameraPhrase}`;
    if (speedPhrase) sentence += ` ${speedPhrase}`;
    return `${capitalizeSentence(sentence)}.`;
  }

  if (scenePhrase) {
    let sentence = `a ${flythroughLabel} of ${scenePhrase}`;
    if (speedPhrase) sentence += `, ${speedPhrase}`;
    return `${capitalizeSentence(sentence)}.`;
  }

  if (cameraPhrase) {
    let sentence = styleAdj
      ? `a ${styleAdj} shot as ${cameraPhrase}`
      : `a cinematic shot as ${cameraPhrase}`;
    if (speedPhrase) sentence += ` ${speedPhrase}`;
    return `${capitalizeSentence(sentence)}.`;
  }

  if (styleAdj && speedPhrase) {
    return `${capitalizeSentence(`a ${styleAdj} flythrough, ${speedPhrase}`)}.`;
  }

  if (styleAdj) {
    return `${capitalizeSentence(`a ${styleAdj} flythrough`)}.`;
  }

  return `${capitalizeSentence(speedPhrase)}.`;
}

export function resolveFlythroughMotionPromptFields({
  motionPromptFields,
  motionPrompt,
} = {}) {
  if (motionPromptFields && typeof motionPromptFields === 'object') {
    return {
      ...FLYTHROUGH_MOTION_PROMPT_FIELDS_DEFAULT,
      ...motionPromptFields,
    };
  }

  const legacyPrompt = String(motionPrompt ?? '').trim();
  if (!legacyPrompt) {
    return { ...FLYTHROUGH_MOTION_PROMPT_FIELDS_DEFAULT };
  }

  return {
    ...FLYTHROUGH_MOTION_PROMPT_FIELDS_DEFAULT,
    scene: legacyPrompt,
  };
}

export function resolveFlythroughMotionPrompt({ motionPromptFields, motionPrompt } = {}) {
  const fields = resolveFlythroughMotionPromptFields({ motionPromptFields, motionPrompt });
  return buildFlythroughMotionPromptFromFields(fields);
}

/** Render history requires a concrete ROI object; full screen uses the full-frame box. */
export function buildFlythroughRenderHistoryRoi(roiMode, roi) {
  if (roiMode === 'full_screen') {
    return { ...FLYTHROUGH_ROI_FULL_SCREEN };
  }
  return clampFlythroughRoi(roi ?? FLYTHROUGH_ROI_FULL_SCREEN);
}

function normalizeRenderHistoryIds(renderHistoryIds = []) {
  return renderHistoryIds
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
}

export function resolveFlythroughWorkflow(renderHistoryIds = [], motionMode) {
  const keyframeIds = normalizeRenderHistoryIds(renderHistoryIds);
  if (keyframeIds.length >= 2) {
    return {
      keyframeIds,
      workflow: FLYTHROUGH_MOTION_MODES.KEYFRAME_STITCHING,
    };
  }
  return {
    keyframeIds,
    workflow: motionMode ?? FLYTHROUGH_MOTION_MODES.CAMERA_PRESET,
  };
}

export function buildFlythroughMotionFields({
  workflow = FLYTHROUGH_MOTION_MODES.CAMERA_PRESET,
  cameraPreset,
  cameraAngleSequence = [],
  motionPrompt,
  motionPromptFields,
}) {
  if (workflow === FLYTHROUGH_MOTION_MODES.KEYFRAME_STITCHING) {
    return {
      camera_preset: null,
      custom_motion_paths: null,
      prompt: null,
    };
  }

  switch (workflow) {
    case FLYTHROUGH_MOTION_MODES.CAMERA_ANGLE_SEQUENCE:
      return {
        camera_preset: null,
        custom_motion_paths: cameraAngleSequence.map((angle) =>
          snapFlythroughCameraAngle(angle),
        ),
        prompt: null,
      };
    case FLYTHROUGH_MOTION_MODES.PROMPT: {
      const trimmed = resolveFlythroughMotionPrompt({ motionPromptFields, motionPrompt });
      return {
        camera_preset: null,
        custom_motion_paths: null,
        prompt: trimmed || null,
      };
    }
    case FLYTHROUGH_MOTION_MODES.CAMERA_PRESET:
    default:
      return {
        camera_preset: cameraPreset || null,
        custom_motion_paths: null,
        prompt: null,
      };
  }
}

export function validateFlythroughMotionInput({
  renderHistoryIds = [],
  motionMode = FLYTHROUGH_MOTION_MODES.CAMERA_PRESET,
  cameraPreset,
  cameraAngleSequence = [],
  motionPrompt,
  motionPromptFields,
}) {
  const { keyframeIds, workflow } = resolveFlythroughWorkflow(renderHistoryIds, motionMode);

  if (keyframeIds.length > 3) {
    return 'Select up to 3 keyframes.';
  }

  if (workflow === FLYTHROUGH_MOTION_MODES.KEYFRAME_STITCHING) {
    if (keyframeIds.length < 2) {
      return 'Select at least 2 keyframes for stitching.';
    }
    return null;
  }

  if (keyframeIds.length > 1) {
    return 'Use no keyframes, one keyframe, or 2–3 keyframes for stitching.';
  }

  switch (workflow) {
    case FLYTHROUGH_MOTION_MODES.CAMERA_ANGLE_SEQUENCE:
      return validateFlythroughCameraAngleSequence(cameraAngleSequence);
    case FLYTHROUGH_MOTION_MODES.PROMPT: {
      const trimmed = resolveFlythroughMotionPrompt({ motionPromptFields, motionPrompt });
      if (!trimmed) return 'Enter scene, camera movement, motion speed, or style.';
      if (trimmed.length > FLYTHROUGH_MOTION_PROMPT_MAX_LENGTH) {
        return `Motion prompt must be ${FLYTHROUGH_MOTION_PROMPT_MAX_LENGTH} characters or fewer.`;
      }
      return null;
    }
    case FLYTHROUGH_MOTION_MODES.CAMERA_PRESET:
    default:
      if (!cameraPreset) return 'Select a camera preset.';
      return null;
  }
}

export function buildFlythroughRenderHistoryConfig({
  motionMode = FLYTHROUGH_MOTION_MODES.CAMERA_PRESET,
  cameraPreset,
  cameraAngleSequence = [],
  motionPrompt,
  motionPromptFields,
  roiMode,
  roi,
  durationSeconds,
  fps,
  resolution,
}) {
  const normalizedCameraPreset =
    typeof cameraPreset === 'string' && cameraPreset.trim() ? cameraPreset.trim() : 'static';
  const config = {
    taskName: 'generate_flythrough',
    cameraPreset: normalizedCameraPreset,
    roi: buildFlythroughRenderHistoryRoi(roiMode, roi),
    durationSec: resolveFlythroughFinalDurationSeconds({
      motionMode,
      durationSeconds,
      cameraAngleSequence,
    }),
    fps: resolveFlythroughFpsApiValue(fps),
    resolution: resolveFlythroughResolutionApiValue(resolution),
  };

  switch (motionMode) {
    case FLYTHROUGH_MOTION_MODES.PROMPT: {
      const prompt = resolveFlythroughMotionPrompt({ motionPromptFields, motionPrompt });
      if (prompt) config.prompt = prompt;
      break;
    }
    case FLYTHROUGH_MOTION_MODES.CAMERA_ANGLE_SEQUENCE: {
      const customMotionPaths = (cameraAngleSequence ?? []).map((angle) =>
        snapFlythroughCameraAngle(angle),
      );
      if (customMotionPaths.length) {
        config.customMotionPaths = customMotionPaths;
      }
      break;
    }
    case FLYTHROUGH_MOTION_MODES.KEYFRAME_STITCHING:
      break;
    case FLYTHROUGH_MOTION_MODES.CAMERA_PRESET:
    default:
      break;
  }

  return config;
}

export function buildFlythroughRenderHistoryRequestBody({
  flythroughS3Key,
  thumbnailS3Key,
  motionMode,
  cameraPreset,
  cameraAngleSequence = [],
  motionPrompt,
  motionPromptFields,
  roiMode,
  roi,
  durationSeconds,
  fps,
  resolution,
}) {
  const body = {
    renderType: 'flythrough',
    flythroughS3Key,
    config: buildFlythroughRenderHistoryConfig({
      motionMode,
      cameraPreset,
      cameraAngleSequence,
      motionPrompt,
      motionPromptFields,
      roiMode,
      roi,
      durationSeconds,
      fps,
      resolution,
    }),
  };

  const normalizedThumbnailS3Key =
    typeof thumbnailS3Key === 'string' ? thumbnailS3Key.trim() : '';
  if (normalizedThumbnailS3Key) {
    body.thumbnailS3Key = normalizedThumbnailS3Key;
  }

  return body;
}

export function extractFlythroughSettingsFromLocalItem(item) {
  if (item?.flythroughSettings && typeof item.flythroughSettings === 'object') {
    return item.flythroughSettings;
  }

  const payload = item?.payload;
  if (!payload || typeof payload !== 'object') return null;

  const renderHistoryId = payload.render_history_id ?? payload.renderHistoryId;
  let motionMode = FLYTHROUGH_MOTION_MODES.CAMERA_PRESET;
  if (payload.prompt) {
    motionMode = FLYTHROUGH_MOTION_MODES.PROMPT;
  } else if (Array.isArray(payload.custom_motion_paths) && payload.custom_motion_paths.length) {
    motionMode = FLYTHROUGH_MOTION_MODES.CAMERA_ANGLE_SEQUENCE;
  } else if (Array.isArray(renderHistoryId) && renderHistoryId.length >= 2) {
    motionMode = FLYTHROUGH_MOTION_MODES.KEYFRAME_STITCHING;
  }

  return {
    motionMode,
    cameraPreset: payload.camera_preset ?? payload.cameraPreset ?? 'dolly_in',
    cameraAngleSequence: payload.custom_motion_paths ?? [],
    motionPromptFields: resolveFlythroughMotionPromptFields({
      motionPromptFields: item?.flythroughSettings?.motionPromptFields,
      motionPrompt: payload.prompt ?? item?.flythroughSettings?.motionPrompt ?? '',
    }),
    roiMode: payload.roi ? 'custom' : 'full_screen',
    roi: payload.roi ?? null,
    durationSeconds: payload.duration_sec ?? payload.durationSec,
    frameRate: String(payload.fps ?? 24),
    renderResolution: resolveFlythroughResolutionIdFromApiValue(payload.resolution),
    renderHistoryIds: Array.isArray(renderHistoryId)
      ? renderHistoryId.map((id) => String(id))
      : renderHistoryId != null
        ? [String(renderHistoryId)]
        : [],
  };
}

export function buildGenerateFlythroughPayload(
  roomImageId,
  {
    renderHistoryIds = [],
    motionMode,
    cameraPreset,
    cameraAngleSequence = [],
    motionPrompt,
    motionPromptFields,
    roiMode,
    roi,
    durationSeconds,
    fps,
    resolution,
    renderTier,
  },
) {
  const { keyframeIds, workflow } = resolveFlythroughWorkflow(renderHistoryIds, motionMode);
  const motionFields = buildFlythroughMotionFields({
    workflow,
    cameraPreset,
    cameraAngleSequence,
    motionPrompt,
    motionPromptFields,
  });

  return {
    task_name: 'generate_flythrough',
    room_image_id: Number(roomImageId),
    render_history_id:
      keyframeIds.length === 0
        ? null
        : keyframeIds.length === 1
          ? keyframeIds[0]
          : keyframeIds,
    prompt: motionFields.prompt,
    camera_preset: motionFields.camera_preset,
    custom_motion_paths: motionFields.custom_motion_paths,
    roi: buildFlythroughRoiPayload(roiMode, roi),
    duration_sec:
      renderTier === 'preview'
        ? FLYTHROUGH_PREVIEW_DURATION_SECONDS
        : resolveFlythroughFinalDurationSeconds({
            motionMode: workflow,
            durationSeconds,
            cameraAngleSequence,
          }),
    fps:
      renderTier === 'preview'
        ? FLYTHROUGH_PREVIEW_FPS
        : resolveFlythroughFpsApiValue(fps),
    resolution:
      renderTier === 'preview'
        ? FLYTHROUGH_PREVIEW_RESOLUTION
        : resolveFlythroughResolutionApiValue(resolution),
    render_tier: renderTier,
  };
}

/**
 * POST /ai-inference generate_flythrough, wait for completion, return video URLs.
 */
async function submitFlythroughVideo(roomImageId, options, { renderTier }) {
  const {
    onStatusChange,
    onTaskStarted,
    fps,
    resolution,
    durationSeconds,
    ...flythroughOptions
  } = options ?? {};
  const motionError = validateFlythroughMotionInput(flythroughOptions);
  if (motionError) {
    throw new Error(motionError);
  }

  const payload = buildGenerateFlythroughPayload(roomImageId, {
    ...flythroughOptions,
    renderTier,
    fps,
    resolution,
    durationSeconds,
  });
  console.log('POST /ai-inference generate_flythrough payload:', payload);

  const reportStatus = (status) => {
    const label = formatInferenceStatusLabel(status);
    if (label) onStatusChange?.(label);
  };

  const res = await runAiInference(payload);
  const immediateKeys = extractFlythroughTaskResultKeys(res);
  const taskId = res?.taskId ?? res?.aiTaskId ?? res?.id;
  if (taskId != null) {
    onTaskStarted?.(String(taskId));
  }
  const inferenceId = res?.id ?? res?.aiTaskId ?? null;
  const initialStatus = res?.status ?? res?.taskStatus ?? res?.state ?? 'pending';
  reportStatus(initialStatus);

  const data =
    immediateKeys.videoS3Key != null
      ? res
      : taskId == null
        ? res
        : await new Promise((resolve, reject) => {
            addTask(taskId, {
              roomImageId,
              onResolve: resolve,
              onError: reject,
              onStatusChange: (status) => reportStatus(status),
            });
          });

  const { videoS3Key, thumbnailS3Key } = extractFlythroughTaskResultKeys(data);
  if (!videoS3Key) {
    throw new Error('Flythrough completed without a result video.');
  }

  const presigned = await getAiInferencePresignedUrl(videoS3Key);
  const rawUrl = presigned?.url ?? presigned?.presignedUrl;
  if (!rawUrl) {
    throw new Error('Could not load the flythrough video.');
  }

  const videoUrl = resolveRoomObjectImageSrcForDisplay(String(rawUrl).trim());
  rememberFlythroughPlaybackUrl(videoS3Key, videoUrl);

  let thumbnailUrl = '';
  if (thumbnailS3Key && !isFlythroughThumbnailPresignUnsupported(thumbnailS3Key)) {
    try {
      const thumbPresigned = await getAiInferencePresignedUrl(thumbnailS3Key);
      const thumbRawUrl = thumbPresigned?.url ?? thumbPresigned?.presignedUrl;
      if (thumbRawUrl) {
        thumbnailUrl = resolveRoomObjectImageSrcForDisplay(String(thumbRawUrl).trim());
      }
    } catch (err) {
      console.warn('Flythrough thumbnail presign unavailable.', err);
    }
  }

  const resolvedTaskId = data?.taskId ?? data?.task_id ?? taskId ?? null;
  const resolvedInferenceId = data?.id ?? data?.aiTaskId ?? inferenceId ?? null;
  const completedAt = data?.completedAt ?? data?.completed_at ?? new Date().toISOString();
  const startedAt = data?.startedAt ?? data?.started_at ?? null;
  const createdAt = data?.createdAt ?? data?.created_at ?? null;

  return {
    status: data?.status ?? data?.taskStatus ?? data?.state,
    resultS3Key: videoS3Key,
    resultVideoS3Key: videoS3Key,
    thumbnailS3Key,
    videoUrl,
    thumbnailUrl,
    payload: data?.payload ?? payload,
    taskId: resolvedTaskId != null ? String(resolvedTaskId) : null,
    inferenceId: resolvedInferenceId != null ? String(resolvedInferenceId) : null,
    createdAt,
    startedAt,
    completedAt,
  };
}

/**
 * POST /ai-inference generate_flythrough (preview tier), wait for completion, return video URL.
 */
export async function submitFlythroughVideoPreview(roomImageId, options) {
  return submitFlythroughVideo(roomImageId, options, { renderTier: 'preview' });
}

/**
 * POST /ai-inference generate_flythrough (final tier), wait for completion, return video URL.
 */
export async function submitFlythroughVideoFinal(roomImageId, options) {
  return submitFlythroughVideo(roomImageId, options, { renderTier: 'final' });
}
